// /app/frontend/src/hooks/usePushNotifications.js
// Hook for managing push notification subscriptions

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// VAPID public key - should be generated and stored in env
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || 'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(true);
  const [swRegistration, setSwRegistration] = useState(null);

  const token = localStorage.getItem('apollo_token');
  const headers = { Authorization: `Bearer ${token}` };

  // Register the service worker
  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator)) {
      return null;
    }
    
    try {
      // Check if already registered
      const existingRegistration = await navigator.serviceWorker.getRegistration('/');
      if (existingRegistration) {
        setSwRegistration(existingRegistration);
        return existingRegistration;
      }
      
      // Register new service worker
      const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      setSwRegistration(registration);
      return registration;
    } catch (error) {
      console.error('Service worker registration failed:', error);
      return null;
    }
  }, []);

  // Check if push notifications are supported
  useEffect(() => {
    const init = async () => {
      const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
      setIsSupported(supported);
      
      if (supported) {
        setPermission(Notification.permission);
        
        // Try to register service worker first
        const registration = await registerServiceWorker();
        
        if (registration) {
          await checkSubscription(registration);
        } else {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    
    init();
  }, [registerServiceWorker]);

  // Check current subscription status
  const checkSubscription = useCallback(async (registration) => {
    try {
      const reg = registration || swRegistration;
      if (!reg) {
        setLoading(false);
        return;
      }
      
      const sub = await reg.pushManager.getSubscription();
      
      setSubscription(sub);
      setIsSubscribed(!!sub);
      
      // Verify with backend
      if (sub && token) {
        try {
          await axios.get(`${API}/api/push/status`, { headers });
        } catch (e) {
          // Subscription exists locally but not on server - re-register
          if (e.response?.status === 404) {
            await registerSubscription(sub);
          }
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    } finally {
      setLoading(false);
    }
  }, [swRegistration, token]);

  // Request permission and subscribe
  const subscribe = async () => {
    if (!isSupported) {
      throw new Error('Push notifications not supported');
    }

    setLoading(true);
    
    try {
      // Request notification permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      
      if (permissionResult !== 'granted') {
        throw new Error('Permission denied');
      }

      // Get or register service worker
      let registration = swRegistration;
      if (!registration) {
        registration = await registerServiceWorker();
        if (!registration) {
          throw new Error('Failed to register service worker');
        }
      }
      
      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // Subscribe to push
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      setSubscription(sub);
      setIsSubscribed(true);

      // Send subscription to backend
      await registerSubscription(sub);

      return sub;
    } catch (error) {
      console.error('Error subscribing:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Register subscription with backend
  const registerSubscription = async (sub) => {
    const subJson = sub.toJSON();
    
    await axios.post(`${API}/api/push/subscribe`, {
      endpoint: subJson.endpoint,
      keys: subJson.keys,
      user_agent: navigator.userAgent
    }, { headers });
  };

  // Unsubscribe
  const unsubscribe = async () => {
    if (!subscription) return;

    setLoading(true);
    
    try {
      // Unsubscribe from push manager
      await subscription.unsubscribe();
      
      // Remove from backend
      await axios.post(`${API}/api/push/unsubscribe`, {
        endpoint: subscription.endpoint
      }, { headers });

      setSubscription(null);
      setIsSubscribed(false);
    } catch (error) {
      console.error('Error unsubscribing:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    subscribe,
    unsubscribe,
    checkSubscription
  };
}

export default usePushNotifications;
